import React, { useEffect, useState } from 'react';
import { FaUserPlus, FaTrash, FaEdit } from 'react-icons/fa';
import * as staffApi from '../services/staffApi';
import { useMerchantAuth } from '../../../contexts/AuthContext';

const STATUS_LABELS = { invited: 'Invited', active: 'Active', suspended: 'Suspended' };
const STATUS_COLORS = { invited: 'bg-amber-100 text-amber-800', active: 'bg-green-100 text-green-800', suspended: 'bg-red-100 text-red-800' };

const isOwnerStaff = (staff) => staff.roles?.some((r) => r.name?.toLowerCase() === 'owner');

export default function StaffTab() {
    const { hasPermission } = useMerchantAuth();
    const [staff, setStaff] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRoleId, setInviteRoleId] = useState('');
    const [inviting, setInviting] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ show: false, staff: null });
    const [roleModal, setRoleModal] = useState({ show: false, staff: null });

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [staffRes, rolesRes] = await Promise.all([
                staffApi.listStaff(),
                staffApi.listRoles()
            ]);
            setStaff(staffRes.data || []);
            setRoles(rolesRes.data || []);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load staff');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim()) {
            return;
        }
        if (!inviteRoleId) {
            setError('Please select a role before inviting.');
            return;
        }
        setInviting(true);
        setError(null);
        try {
            const res = await staffApi.inviteStaff(inviteEmail.trim(), [inviteRoleId]);
            setInviteEmail('');
            setInviteRoleId('');
            fetchData();
            if (res?.data?.emailSent === false) {
                setError('Staff invited, but the email could not be sent. Check RESEND_API_KEY and Resend domain verification.');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to invite staff');
        } finally {
            setInviting(false);
        }
    };

    const handleRemove = async () => {
        if (!deleteModal.staff) {
            return;
        }
        try {
            await staffApi.removeStaff(deleteModal.staff.id);
            setDeleteModal({ show: false, staff: null });
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to remove staff');
        }
    };

    const handleAssignRole = async (staffId, roleId) => {
        try {
            await staffApi.assignRole(staffId, roleId);
            fetchData();
            setRoleModal({ show: false, staff: null });
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to assign role');
        }
    };

    const handleUnassignRole = async (staffId, roleId) => {
        try {
            await staffApi.unassignRole(staffId, roleId);
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to unassign role');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-tutorial="staff-page">
            <h2 className="text-xl font-bold text-gray-800">Staff</h2>

            {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
            )}

            {/* Invite form */}
            {hasPermission('invite_staff') && (
            <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
                    <FaUserPlus /> Invite New Staff
                </h3>
                <form onSubmit={handleInvite} className="space-y-3">
                    <div className="flex gap-2 flex-wrap items-end">
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-xs text-gray-500 mb-1">Email</label>
                            <input
                                type="email"
                                placeholder="staff@example.com"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                        </div>
                        <div className="min-w-[160px]">
                            <label className="block text-xs text-gray-500 mb-1">Role</label>
                            <select
                                value={inviteRoleId}
                                onChange={(e) => setInviteRoleId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                                <option value="">Select role</option>
                                {roles
                                    .filter((r) => r.name !== 'owner')
                                    .map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <button
                            type="submit"
                            disabled={inviting || !inviteEmail.trim() || !inviteRoleId}
                            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50"
                        >
                            {inviting ? 'Inviting...' : 'Invite'}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500">They will receive an invite to sign up and join your team.</p>
                </form>
            </div>
            )}

            {/* Staff list */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <h3 className="text-md font-semibold p-4 border-b">Current Staff</h3>
                {staff.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No staff members yet. Invite someone above.</div>
                ) : (
                    <div className="divide-y">
                        {staff.map((s) => (
                            <div key={s.id} className="p-4 flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <div className="font-medium text-gray-800">{s.email}</div>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[s.status] || 'bg-gray-100'}`}>
                                            {STATUS_LABELS[s.status] || s.status}
                                        </span>
                                        {s.roles?.map((r) => (
                                            <span
                                                key={r.id}
                                                className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-700 flex items-center gap-1 inline-flex"
                                            >
                                                {r.name}
                                                {hasPermission('edit_staff') && (
                                                <button
                                                    onClick={() => handleUnassignRole(s.id, r.id)}
                                                    className="text-gray-500 hover:text-red-600 ml-0.5"
                                                    title="Remove role"
                                                >
                                                    ×
                                                </button>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {hasPermission('edit_staff') && (
                                    <button
                                        onClick={() => setRoleModal({ show: true, staff: s })}
                                        className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                        title="Manage roles"
                                    >
                                        <FaEdit />
                                    </button>
                                    )}
                                    {!isOwnerStaff(s) && hasPermission('delete_staff') && (
                                        <button
                                            onClick={() => setDeleteModal({ show: true, staff: s })}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            title="Remove staff"
                                        >
                                            <FaTrash />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Role assignment modal */}
            {roleModal.show && roleModal.staff && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold mb-4">Assign role to {roleModal.staff.email}</h3>
                        <div className="space-y-2">
                            {roles
                                .filter((r) => !roleModal.staff.roles?.some((sr) => sr.id === r.id))
                                .map((r) => (
                                    <button
                                        key={r.id}
                                        onClick={() => handleAssignRole(roleModal.staff.id, r.id)}
                                        className="w-full text-left px-3 py-2 rounded bg-gray-50 hover:bg-primary/10"
                                    >
                                        {r.name} {r.is_default && '(default)'}
                                    </button>
                                ))}
                            {!roles.some((r) => !roleModal.staff.roles?.some((sr) => sr.id === r.id)) && (
                                <p className="text-sm text-gray-500">All roles already assigned.</p>
                            )}
                        </div>
                        <button
                            onClick={() => setRoleModal({ show: false, staff: null })}
                            className="mt-4 w-full py-2 border border-gray-300 rounded"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Delete confirmation modal */}
            {deleteModal.show && deleteModal.staff && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold mb-2">Remove Staff</h3>
                        <p className="text-gray-600 mb-4">
                            Remove {deleteModal.staff.email}? They will lose access to this merchant.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setDeleteModal({ show: false, staff: null })}
                                className="flex-1 py-2 border rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRemove}
                                className="flex-1 py-2 bg-red-600 text-white rounded"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
